/**
 * <ad-slot> web component.
 *
 * A provider-agnostic container for ad placements. Uses Shadow DOM for its own
 * chrome (sizing skeleton) while projecting the GPT target <div> via <slot>
 * so that ad scripts can find it in the light DOM.
 *
 * Attributes (set server-side):
 *   variant   — "banner" | "half-page" | "rectangle"
 *   slot-id   — GPT div ID (e.g. "div-gpt-ad-global-top-0")
 *   unit-path — Ad unit suffix (e.g. "728x90_Top"); providers prepend the brand/network prefix
 *   position  — "top" | "bottom" | "sidebar" | "grid"
 *   no-collapse — Boolean; when present, slot keeps its dimensions when empty
 *
 * The component registers itself with the page-level ad provider on connect,
 * and deregisters on disconnect. It knows nothing about GPT, Freestar, or bidding.
 */

const STYLES = `
  :host {
    display: block;
    contain: layout style paint;
    overflow: hidden;
    position: relative;
  }

  /* --- Banner: responsive height --- */
  :host([variant="banner"]) {
    min-height: 50px;
    width: 100%;
    max-width: 320px;
    margin-inline: auto;
  }
  @media (min-width: 1024px) {
    :host([variant="banner"]) {
      min-height: 90px;
      max-width: 728px;
    }
  }

  /* --- Half Page --- */
  :host([variant="half-page"]) {
    min-width: 300px;
    min-height: 600px;
  }

  /* --- Rectangle Medium --- */
  :host([variant="rectangle"]) {
    min-width: 300px;
    min-height: 250px;
  }

  .ad-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  /* Fallback is visible by default, hidden when an ad fills */
  ::slotted([slot="fallback"]) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
  :host(.ad-filled) ::slotted([slot="fallback"]) {
    display: none;
  }
`;

class AdSlot extends HTMLElement {
  #intersectionObserver = null;
  #fillObserver = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.shadowRoot.innerHTML = `<style>${STYLES}</style><div class="ad-container"><slot></slot><slot name="fallback"></slot></div>`;

    // Watch the light-DOM GPT div for child additions (iframe injected = ad filled)
    const gptDiv = this.querySelector(`#${CSS.escape(this.slotId)}`);
    if (gptDiv) {
      this.#fillObserver = new MutationObserver(() => {
        if (gptDiv.children.length > 0) {
          this.classList.add("ad-filled");
          this.#fillObserver?.disconnect();
          this.#fillObserver = null;
        }
      });
      this.#fillObserver.observe(gptDiv, { childList: true });
    }

    // Lazy-load: register with provider only when entering the viewport area
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#intersectionObserver.disconnect();
            this.#intersectionObserver = null;
            this.#register();
            break;
          }
        }
      },
      { rootMargin: "200px" },
    );
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#fillObserver?.disconnect();
    this.#fillObserver = null;
    globalThis.__adProvider?.unregisterSlot(this);
  }

  #register() {
    if (globalThis.__adProvider) {
      globalThis.__adProvider.registerSlot(this);
    } else {
      // Provider not yet initialised — wait for it
      globalThis.addEventListener(
        "ad-provider:ready",
        () => globalThis.__adProvider?.registerSlot(this),
        { once: true },
      );
    }
  }

  /** Convenience getters used by providers */
  get slotId() {
    return this.getAttribute("slot-id");
  }
  get unitPath() {
    return this.getAttribute("unit-path");
  }
  get variant() {
    return this.getAttribute("variant");
  }
  get position() {
    return this.getAttribute("position");
  }
  get noCollapse() {
    return this.hasAttribute("no-collapse");
  }
  get refreshGroup() {
    return this.getAttribute("refresh-group") || null;
  }
}

customElements.define("ad-slot", AdSlot);
